import { cardsSelector } from '@/features/cards/cardsSelectors';
import { applyProfileCardLoadingSelector } from '@/features/loading/loadingSelectors';
import {
  applyProfileCard,
  deactivateProfileCards,
} from '@/features/profile/profileActions';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import type {
  CardDTO,
  CustomerProfileCardGetModelDTO,
} from '@/types/dto/agent';
import { showErrorToast } from '@/utils/errorToast';
import { partitionProfileCards } from '@/utils/partitionProfileCards';
import {
  Badge,
  Button,
  Icons,
  Select,
  Typography,
} from '@packages/webshop-design-system';
import { useCallback, useMemo, useState, type FC } from 'react';

const getCardLabel = (card: {
  id: string;
  code?: string;
  name?: { text?: string } | null;
}): string => card.name?.text ?? card.code ?? card.id;

// CardModel swagger omits this; present at runtime on some card endpoints.
const isRegionalMasterCard = (card: CardDTO) =>
  Boolean(
    (card as CardDTO & { isRegionalReductionCard?: boolean })
      .isRegionalReductionCard
  );

const DiscountEligibility: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const profile = useSelector(profileDataSelector);
  const masterCards = useSelector(cardsSelector);
  const isApplyingCard = useSelector(applyProfileCardLoadingSelector);
  const [pendingCardId, setPendingCardId] = useState<string | null>(null);

  const profileCards = useMemo(() => profile?.cards ?? [], [profile?.cards]);

  // A regional card is BE-managed: it is linked on login from the residence
  // data, so it is never removable and never offered in the dropdown. Either
  // source may carry the flag, so trust both.
  const regionalCardTypeIds = useMemo(() => {
    const ids = new Set<string>();

    masterCards
      .filter(isRegionalMasterCard)
      .forEach((card) => ids.add(card.id));
    profileCards
      .filter((card) => card.isRegionalReductionCard)
      .forEach((card) => ids.add(card.cardTypeId));

    return ids;
  }, [masterCards, profileCards]);

  const { regional, selectable } = useMemo(
    () => partitionProfileCards(profileCards, regionalCardTypeIds),
    [profileCards, regionalCardTypeIds]
  );

  const masterCardsById = useMemo(
    () => new Map(masterCards.map((card) => [card.id, card])),
    [masterCards]
  );

  const getProfileCardLabel = useCallback(
    (card: CustomerProfileCardGetModelDTO) => {
      const masterCard = masterCardsById.get(card.cardTypeId);
      return masterCard ? getCardLabel(masterCard) : card.cardTypeId;
    },
    [masterCardsById]
  );

  const attachedCardTypeIds = useMemo(
    () => new Set([...regional, ...selectable].map((card) => card.cardTypeId)),
    [regional, selectable]
  );

  const availableCardOptions = useMemo(
    () =>
      masterCards
        .filter(
          (card) =>
            !regionalCardTypeIds.has(card.id) &&
            !attachedCardTypeIds.has(card.id)
        )
        .map((card) => ({
          value: card.id,
          label: getCardLabel(card),
        })),
    [attachedCardTypeIds, masterCards, regionalCardTypeIds]
  );

  const handleRemoveCard = useCallback(
    async (cardId: string) => {
      setPendingCardId(cardId);
      try {
        await dispatch(deactivateProfileCards([cardId])).unwrap();
      } catch (error) {
        showErrorToast(
          <TransText i18nKey="updatingInformationFailed" />,
          error as ApiErrorResponse
        );
      } finally {
        setPendingCardId(null);
      }
    },
    [dispatch]
  );

  const handleApplyCard = useCallback(
    async (cardTypeId: string | null) => {
      if (!cardTypeId) {
        return;
      }

      try {
        await dispatch(applyProfileCard({ cardTypeId })).unwrap();
      } catch (error) {
        showErrorToast(
          <TransText i18nKey="updatingInformationFailed" />,
          error as ApiErrorResponse
        );
      }
    },
    [dispatch]
  );

  return (
    <div className="mt-6 flex flex-col gap-4">
      <Typography variant="body1-bold">
        <TransText i18nKey="discountEligibility" />
      </Typography>

      {regional.map((card) => (
        <Badge key={card.id} className="w-fit" variant="disabled">
          {getProfileCardLabel(card)}
        </Badge>
      ))}

      {selectable.map((card) => (
        <div
          key={card.id}
          className="flex items-center justify-between gap-4"
          data-testid="discount-card-row"
        >
          <Typography variant="body1">{getProfileCardLabel(card)}</Typography>
          <Button
            type="button"
            variant="ghost"
            attention
            className="h-auto tablet:px-1 tablet:py-0.5"
            onClick={() => handleRemoveCard(card.id)}
            isPending={pendingCardId === card.id}
            startAdornment={<Icons.removeCircle height={16} width={16} />}
            data-testid="discount-card-remove-button"
          >
            <TransText i18nKey="remove" />
          </Button>
        </div>
      ))}

      {availableCardOptions.length === 0 ? (
        <Typography variant="body2" className="text-neutral">
          <TransText i18nKey="noDiscountCardsAvailable" />
        </Typography>
      ) : (
        <Select
          options={availableCardOptions}
          selectedKey={null}
          onSelectionChange={(key) => handleApplyCard(key as string | null)}
          placeholder={t('text.selectDiscountCard')}
          isDisabled={isApplyingCard}
          aria-label={t('text.selectDiscountCard')}
          data-testid="discount-card-select"
        />
      )}
    </div>
  );
};

export default DiscountEligibility;
